import {Fragment, ReactElement, createElement, isValidElement} from 'react';
import {Primitive} from 'ts-essentials';
import type {Options} from './createTranslator';
import {ExtractMessageValues, ParsedMessage} from './parse';

export type TranslationValue = Primitive | ReactElement;

export function compile<
  Message extends string,
  PlaceholderKey extends ExtractMessageValues<Message>,
>(
  parsedMessage: ParsedMessage<Message>,
  values: Record<PlaceholderKey[number], TranslationValue> | undefined,
  options: Options,
): string | ReactElement {
  let message: string = parsedMessage.rawMessage;

  if (!values) {
    return message;
  }

  const elements: Record<string, ReactElement> = {};

  Object.entries<TranslationValue>(values).forEach(([key, value]) => {
    if (isValidElement(value)) {
      elements[key] = value;
      return;
    }

    message = message.replace(
      new RegExp(`${options.prefix}-?\\s?${key}\\s?${options.suffix}`, 'gm'),
      String(value),
    );
  });

  const elementKeys = Object.keys(elements);

  if (elementKeys.length === 0) {
    return message;
  }

  // The capture group keeps the matched key at every odd index of the split
  const parts = message.split(
    new RegExp(
      `${options.prefix}-?\\s?(${elementKeys.join('|')})\\s?${options.suffix}`,
      'gm',
    ),
  );

  return createElement(
    Fragment,
    null,
    ...parts.map((part, index) => (index % 2 === 1 ? elements[part] : part)),
  );
}
